/**
 * Finance panel: the same mathematics as the storm, applied to an option.
 *
 * Prices a European option two ways (closed-form Black-Scholes and the
 * Crank-Nicolson heat-equation solver) and shows the difference, next to
 * a note on where the same solver runs in the simulation. Recomputes only
 * when an input changes, never per frame.
 */
import { bsCall, bsPut, type BsInputs } from '../finance/blackScholes';
import { DEFAULT_FD_GRID, fdPrice } from '../finance/heatEqPricer';

interface Field {
  key: 'spot' | 'strike' | 'vol' | 'rate' | 'timeYears' | 'dividendYield';
  label: string;
  value: number;
  step: number;
}

const FIELDS: Field[] = [
  { key: 'spot', label: 'Spot S', value: 100, step: 1 },
  { key: 'strike', label: 'Strike K', value: 105, step: 1 },
  { key: 'vol', label: 'Vol σ', value: 0.25, step: 0.01 },
  { key: 'rate', label: 'Rate r', value: 0.05, step: 0.005 },
  { key: 'dividendYield', label: 'Yield q', value: 0.02, step: 0.005 },
  { key: 'timeYears', label: 'Expiry T (y)', value: 1, step: 0.05 },
];

export function createFinancePanel(root: HTMLElement, gustInfo: () => string): void {
  root.classList.add('finance');
  const header = document.createElement('button');
  header.type = 'button';
  header.className = 'hud-header';
  header.textContent = 'Finance: the same PDEs ▸';
  const body = document.createElement('div');
  body.hidden = true;
  root.append(header, body);
  header.addEventListener('click', () => {
    body.hidden = !body.hidden;
    header.textContent = body.hidden ? 'Finance: the same PDEs ▸' : 'Finance: the same PDEs ▾';
    if (!body.hidden) update();
  });

  const inputs: Record<string, HTMLInputElement> = {};
  for (const f of FIELDS) {
    const row = document.createElement('label');
    row.className = 'controls-row';
    const name = document.createElement('span');
    name.textContent = f.label;
    const input = document.createElement('input');
    input.type = 'number';
    input.step = String(f.step);
    input.value = String(f.value);
    input.addEventListener('input', update);
    row.append(name, input, document.createElement('span'));
    body.appendChild(row);
    inputs[f.key] = input;
  }
  const kindRow = document.createElement('label');
  kindRow.className = 'controls-row';
  const kindName = document.createElement('span');
  kindName.textContent = 'Type';
  const kind = document.createElement('select');
  for (const k of ['call', 'put']) {
    const o = document.createElement('option');
    o.value = k;
    o.textContent = k;
    kind.appendChild(o);
  }
  kind.addEventListener('change', update);
  kindRow.append(kindName, kind, document.createElement('span'));
  body.appendChild(kindRow);

  const out = document.createElement('pre');
  out.className = 'hud-stats';
  body.appendChild(out);
  const note = document.createElement('p');
  note.className = 'finance-note';
  body.appendChild(note);

  function read(): BsInputs | null {
    const v = (k: Field['key']): number => Number((inputs[k] as HTMLInputElement).value);
    const i: BsInputs = {
      spot: v('spot'),
      strike: v('strike'),
      vol: v('vol'),
      rate: v('rate'),
      timeYears: v('timeYears'),
      dividendYield: v('dividendYield'),
    };
    const ok =
      i.spot > 0 && i.strike > 0 && i.vol > 0 && i.timeYears > 0 && Number.isFinite(i.rate);
    return ok ? i : null;
  }

  function update(): void {
    const i = read();
    if (!i) {
      out.textContent = 'Inputs must be positive (rate may be any number).';
      return;
    }
    const isCall = kind.value === 'call';
    const closed = isCall ? bsCall(i) : bsPut(i);
    const t0 = performance.now();
    const fd = fdPrice(i, isCall ? 'call' : 'put');
    const ms = performance.now() - t0;
    out.textContent =
      `Black-Scholes   ${closed.toFixed(6)}\n` +
      `Crank-Nicolson  ${fd.toFixed(6)}\n` +
      `difference      ${Math.abs(fd - closed).toExponential(2)}\n` +
      `grid            ${DEFAULT_FD_GRID.nSpace} x ${DEFAULT_FD_GRID.nTime}, ${ms.toFixed(1)} ms`;
    note.textContent =
      'In log-price, Black-Scholes is a heat equation. The solver above takes implicit ' +
      '(backward-Euler and Crank-Nicolson) steps of it, the same kind of step the cloud ' +
      "layer's diffusion takes every frame. Cloud advection is the method of characteristics. " +
      `The wind gusts follow an Ornstein-Uhlenbeck process, the Vasicek short-rate model: ${gustInfo()}.`;
  }
}
